import { createFileRoute } from "@tanstack/react-router";
import SimpleTable from "@/components/ui/table";
import { useSimpleForm } from "@/hooks/use-simple-form";
import { useEffect } from "react";
import FullPagination from "@/components/ui/shared/full-pagination";
import { SearchEngine } from "@/components/ui/shared/search-engine";
import { Package } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useTranslate } from "@/hooks/use-translate";
import { Plus } from "lucide-react";
import { useNavigate } from "@tanstack/react-router";
import useAppStore from "@/store";
import { useTableAttributes } from "@/hooks/use-table-attributes";
import TableSettings from "@/components/ui/shared/table-settings";
import type { Service } from "@/store/Service";
import { ServiceTableColumns } from "@/components/service/service-meta";
import { ServiceModals } from "@/components/service/service-modals";
import { isset } from "@/lib/utils";

export const Route = createFileRoute("/(dasboard)/products/services/")({
  component: ServicesPage,
});

function ServicesPage() {
  const navigate = useNavigate();
  const { t } = useTranslate();
  const store = useAppStore();
  const tableName = "services";
  const tableAttributes = useTableAttributes();

  const searchForm = useSimpleForm({
    keyword: undefined,
    category_id: undefined,
  });

  const search = ({ page = 1, per_page = 10 } = {}) => {
    store.service.fetch({ page, per_page, ...searchForm.values });
  };

  const handleAutoComplete = (values: any) => {
    store.service.paginate(
      store.service.items.filter((item: Service) => {
        const nameMatch = isset(values.keyword)
          ? item.name.toLowerCase().includes(values.keyword.toLowerCase())
          : true;

        const categoryMatch = values.category_id
          ? item.category_id === values.category_id
          : true;

        return nameMatch && categoryMatch;
      })
    );
  };

  useEffect(() => {
    if (store.auth.session) {
      search({});
      store.category.fetch();
    }

    tableAttributes.loadAttributes(tableName, [
      "name",
      "category",
      "selling_price",
      "quantity",
      "unit_price",
      "creator",
      "actions",
    ]);
  }, [store.auth.session]);

  const columns = ServiceTableColumns({
    store,
    onEdit: (item: any) => {
      store.service.setCurrent(item);
      navigate({ to: `/products/services/${item.id}` });
    },
    onDelete: async (item: any) => {
      store.service.setCurrent(item);
      store.display.show("delete_service");
    },
  }).filter((item) =>
    (tableAttributes.attributes[tableName] ?? []).includes(item.name)
  );

  return (
    <div>
      <header className="flex items-center justify-between mb-4">
        <h2 className="text-2xl font-semibold"> {t("Services")} </h2>
        <Button
          onClick={() => {
            navigate({ to: "/products/services/create" });
          }}
        >
          <Plus className="w-4 h-4" />
          {t("Add service")}
        </Button>
      </header>

      <ServiceModals />

      <TableSettings
        columns={ServiceTableColumns({ store })}
        tableName={tableName}
      >
        <SearchEngine
          autoComplete={handleAutoComplete}
          selects={[
            {
              placeholder: t("All categories"),
              options: store.category.items.map((item) => ({
                value: item.id,
                label: item.name,
              })),
              name: "category_id",
              icon: <Package className="w-4 h-4" />,
              size: "w-[250px]",
            },
          ]}
          form={searchForm}
          onSubmit={() => search()} // Trigger full fetch on form submit
        />
      </TableSettings>

      <SimpleTable items={store.service.pagination.data} columns={columns} />

      <FullPagination
        className="mt-4"
        canEdit={true}
        pagination={store.service.pagination}
        onGoto={(page: number) =>
          store.service.paginate(store.service.result, { page })
        }
        onPerPage={(per_page) =>
          store.service.paginate(store.service.result, { per_page })
        }
      />
    </div>
  );
}
